import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { TripDetailsV2 } from "@b2bportal/air-shopping-api";
import { type SortOptionEnum, TripCategory } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useChooseFare, useSelectReturnFlight } from "@tenant/flights";
import {
  FlightSearchSelectors,
  FlightShopSelectors,
  type FlightState,
  setSortOption,
} from "../../../store";

export const useReturnPageContainer = ({
  goToNextStep,
}: {
  goToNextStep: () => void;
}) => {
  const { t } = useI18nContext();

  const multicityShopProgress = useSelector(
    FlightShopSelectors.multicityFlightShopProgressSelector
  );

  const flightList = useSelector((state: FlightState) =>
    FlightShopSelectors.getFilteredAndSortedReturnFlightList(
      state,
      multicityShopProgress
    )
  );
  const selectedTrip = useSelector(FlightShopSelectors.getSelectedTrip);
  const tripDetails: TripDetailsV2 | undefined = useSelector(
    FlightShopSelectors.tripDetailsByIdSelector
  )?.[selectedTrip.tripId ?? ""];
  const airports = useSelector(FlightShopSelectors.getAirports);
  const origin = useSelector(FlightSearchSelectors.getOrigin);
  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);

  const dispatch = useDispatch();
  const sortOption = useSelector(FlightShopSelectors.getSortOption);
  const handleSortChange = (value: string) => {
    dispatch(setSortOption(value as SortOptionEnum));
  };

  const handleFareSelect = useSelectReturnFlight(multicityShopProgress);

  const handleChooseFare = useChooseFare(goToNextStep);

  const text = useMemo(() => {
    const subtitle = () => {
      switch (tripCategory) {
        case TripCategory.ONE_WAY:
          return t("core-flights.shopPage.subtitle.oneWay");
        case TripCategory.ROUND_TRIP:
          return t("core-flights.shopPage.subtitle.roundTrip");
      }
    };

    return {
      pageTitle: t("core-flights.returnPage.title.short"),
      subtitle: subtitle(),
    };
  }, [tripCategory, t]);

  return {
    context: {
      flightList,
      selectedTrip,
      tripDetails,
      airports,
      origin,
      tripCategory,
      sortOption,
    },
    handlers: {
      handleSortChange,
      handleFareSelect,
      handleChooseFare,
    },
    text,
  };
};
